// components/calendar/AgendaCalendarBase.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import {
    Alert, Badge, Box, Button, Card, CardContent, Chip, Fade, GlobalStyles,
    IconButton, InputAdornment, LinearProgress, Paper, Stack, TextField,
    Tooltip, Typography, Zoom,
} from '@mui/material';

import {
    CalendarMonth as CalendarMonthIcon,
    Clear as ClearIcon,
    DirectionsBus as DirectionsBusIcon,
    Download as DownloadIcon,
    EventAvailable as EventAvailableIcon,
    FilterAlt as FilterAltIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Timeline as TimelineIcon,
} from '@mui/icons-material';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import {
    addDays, addMinutes, differenceInMinutes, endOfDay, format, getDay,
    isSameDay, parse, parseISO, startOfDay, subDays,
} from 'date-fns';
import { es } from 'date-fns/locale';

import useCalendarEvents from '../../../hooks/useCalendarEvents';
import EventComponent from './AgendaCalendarEvent';
import AgendaCalendarDetailDialog from './AgendaCalendarDetailDialog';
import {
    CALENDAR_MESSAGES, ESTADO_CONFIG, STORAGE_KEYS,
    getEstadoConfig, getManifestCode, parseSafeDate,
} from './agendaCalendar.constants';

// =============================================================
// Localizer
// =============================================================
const locales = { es };
const localizer = dateFnsLocalizer({
    format, parse, startOfWeek: () => 1, getDay, locales,
});

// =============================================================
// Utilidades locales
// =============================================================
const usePersistedState = (key, initialState) => {
    const [state, setState] = useState(() => {
        try {
            const stored = localStorage.getItem(key);
            return stored ? JSON.parse(stored) : initialState;
        } catch { return initialState; }
    });
    const updateState = useCallback((newState) => {
        setState((prev) => {
            const next = typeof newState === 'function' ? newState(prev) : newState;
            localStorage.setItem(key, JSON.stringify(next));
            return next;
        });
    }, [key]);
    return [state, updateState];
};

const MetricCard = ({ title, value, subtitle, icon, color = 'primary.main' }) => (
    <Zoom in style={{ transitionDelay: '100ms' }}>
        <Card elevation={0} sx={{
            height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 3,
            bgcolor: 'background.paper', transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
        }}>
            <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: 2.5, display: 'grid', placeItems: 'center',
                        bgcolor: `${color}10`, color,
                    }}>
                        {icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>{title}</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2, mt: 0.5 }}>{value}</Typography>
                        {subtitle && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{subtitle}</Typography>}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    </Zoom>
);

const FilterChip = ({ label, active, onClick, icon }) => (
    <Chip
        label={label}
        onClick={onClick}
        icon={icon}
        variant={active ? 'filled' : 'outlined'}
        color={active ? 'primary' : 'default'}
        sx={{ fontWeight: 600, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-1px)' } }}
    />
);

// =============================================================
// COMPONENTE BASE
// =============================================================
const AgendaCalendarBase = ({
    // ---- Filtros base (mismos nombres que en el backend)
    serProInt,
    tipoServicioId,
    tipoServicioCodigo,
    proyectoId,

    // ---- UI
    title = 'Agenda de Manifiestos',
    subtitle = 'Visualización operativa por fecha, estado, vehículo y conductor',
    filterBadges = [],           // ej: [{ label: 'Internos', color: 'primary' }]
    allowedEstados,              // opcional: limitar chips, ej: ['EN_PROCESO','COMPLETADO']
    allowExport = true,
    defaultView = 'month',
}) => {
    // ---------- Estado ----------
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [estadoFilter, setEstadoFilter] = usePersistedState(STORAGE_KEYS.ESTADO_FILTER, 'TODOS');
    const [search, setSearch] = useState('');
    const [calendarView, setCalendarView] = usePersistedState(STORAGE_KEYS.CALENDAR_VIEW, defaultView);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [filtersExpanded, setFiltersExpanded] = useState(false);

    const [dateRange, setDateRange] = useState(() => ({
        start: subDays(new Date(), 7),
        end: addDays(new Date(), 30),
    }));
    const [appliedRange, setAppliedRange] = useState(null);

    const searchTimeoutRef = useRef();

    // ---------- Data ----------
    const { data, isLoading, isFetching, error, refetch } = useCalendarEvents({
        start: appliedRange?.start,
        end: appliedRange?.end,
        serProInt,
        tipoServicioId,
        tipoServicioCodigo,
        proyectoId,
        enabled: !!appliedRange,
    });

    const eventsData = data?.events ?? [];
    const pagination = data?.pagination ?? null;

    // ---------- Procesar eventos ----------
    const events = useMemo(() => {
        //if (!data || data.length === 0) return [];
        if (!eventsData.length) return [];
        return eventsData.map((item) => {
            let start = parseSafeDate(item.hora_salida);
            if (!start) return null;

            let end = parseSafeDate(item.hora_llegada);
            if (!end || end <= start) {
                const dur = item.duracion_minutos > 0 ? item.duracion_minutos : 90;
                end = addMinutes(start, dur);
            }

            const placa = item.vehiculo?.VehMat || 'Sin placa';
            const conductoresStr = item.conductores?.length > 0
                ? item.conductores.map((c) => c.NomUsu).join(', ')
                : 'Sin conductor';

            return {
                id: item.manid,
                title: item.maninf || `Manifiesto ${getManifestCode(item.manid)}`,
                start,
                end,
                resource: {
                    ...item,
                    codigo: getManifestCode(item.manid),
                    horaStr: format(start, 'HH:mm', { locale: es }),
                    fechaStr: format(start, 'dd/MM/yyyy', { locale: es }),
                    placa,
                    conductoresStr,
                    duracion_minutos: differenceInMinutes(end, start),
                },
            };
        }).filter(Boolean);
    }, [eventsData]);

    // ---------- Filtro cliente ----------
    const filteredEvents = useMemo(() => {
        const term = search.trim().toLowerCase();
        return events.filter((event) => {
            const { resource } = event;
            const matchesEstado = estadoFilter === 'TODOS' || resource.manest === estadoFilter;
            const matchesSearch = !term || [
                resource.codigo, resource.maninf, resource.manori, resource.mandes,
                resource.placa, resource.conductoresStr,
            ].some((v) => v && String(v).toLowerCase().includes(term));
            return matchesEstado && matchesSearch;
        });
    }, [events, estadoFilter, search]);

    // ---------- Stats ----------
    const stats = useMemo(() => {
        const total = filteredEvents.length;
        const enProceso = filteredEvents.filter((e) => e.resource.manest === 'EN_PROCESO').length;
        const completados = filteredEvents.filter((e) => e.resource.manest === 'COMPLETADO').length;
        const proximos = filteredEvents.filter((e) => e.start > new Date() && e.start < addMinutes(new Date(), 1440)).length;
        return { total, enProceso, completados, proximos };
    }, [filteredEvents]);

    // ---------- Handlers ----------
    const handleApplyRange = useCallback(() => {
        if (!dateRange.start || !dateRange.end) return;
        setAppliedRange({
            start: format(startOfDay(dateRange.start), 'yyyy-MM-dd'),
            end: format(endOfDay(dateRange.end), 'yyyy-MM-dd'),
        });
    }, [dateRange]);

    const handleClearFilters = useCallback(() => {
        setDateRange({ start: subDays(new Date(), 7), end: addDays(new Date(), 30) });
        setAppliedRange(null);
        setEstadoFilter('TODOS');
        setSearch('');
        setFiltersExpanded(false);
    }, [setEstadoFilter]);

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => setSearch(value), 300);
    }, []);

    const handleRefresh = useCallback(() => { if (appliedRange) refetch(); }, [refetch, appliedRange]);

    const handleExport = useCallback(() => {
        const exportData = filteredEvents.map((event) => ({
            Código: event.resource.codigo,
            Descripción: event.title,
            Fecha: event.resource.fechaStr,
            Hora: event.resource.horaStr,
            Origen: event.resource.manori,
            Destino: event.resource.mandes,
            Estado: getEstadoConfig(event.resource.manest).label,
            Placa: event.resource.placa,
            Conductores: event.resource.conductoresStr,
            Duración: event.resource.duracion_minutos ? `${event.resource.duracion_minutos} min` : '-',
        }));
        const csv = [
            Object.keys(exportData[0] || {}).join(','),
            ...exportData.map((row) => Object.values(row).map((v) => `"${v ?? ''}"`).join(',')),
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `manifiestos_${format(new Date(), 'yyyy-MM-dd_HHmm')}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    }, [filteredEvents]);

    // ---------- Efectos ----------
    useEffect(() => {
        if (dateRange.start && dateRange.end && !appliedRange) handleApplyRange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset estado chips si cambian los permitidos
    useEffect(() => {
        if (allowedEstados && estadoFilter !== 'TODOS' && !allowedEstados.includes(estadoFilter)) {
            setEstadoFilter('TODOS');
        }
    }, [allowedEstados, estadoFilter, setEstadoFilter]);

    // ---------- Estilos del calendario ----------
    const calendarStyles = {
        '.rbc-calendar': { fontFamily: 'inherit', height: '100%', display: 'flex', flexDirection: 'column' },
        '.rbc-toolbar': { gap: '8px', marginBottom: '20px', flexWrap: 'wrap', flexShrink: 0 },
        '.rbc-toolbar button': {
            border: '1px solid #e0e0e0', borderRadius: '10px', padding: '8px 16px', fontWeight: 600,
            transition: 'all 0.2s',
            '&:hover': { background: '#f5f5f5', transform: 'translateY(-1px)' },
            '&.rbc-active': { background: '#1976d2', borderColor: '#1976d2', color: '#fff', boxShadow: '0 2px 8px rgba(25,118,210,0.3)' },
        },
        '.rbc-toolbar-label': { fontWeight: 800, fontSize: '1.25rem', color: '#1a2a3a' },
        '.rbc-header': {
            padding: '12px 8px', fontWeight: 700, fontSize: '0.8rem', color: '#546e7a',
            background: '#fafafa', borderBottom: '2px solid #e0e0e0',
            textTransform: 'uppercase', letterSpacing: 0.5,
        },
        '.rbc-month-view, .rbc-time-view, .rbc-agenda-view': {
            border: '1px solid #e0e0e0', borderRadius: '16px', overflow: 'hidden',
            background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            flex: 1, display: 'flex', flexDirection: 'column',
        },
        '.rbc-agenda-view': { '& .rbc-agenda-content': { flex: 1, overflowY: 'auto !important' } },
        '.rbc-today': { backgroundColor: '#f4f8ff' },
        '.rbc-event': {
            border: 'none', borderRadius: '10px', cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'scale(1.02)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000 },
        },
    };

    // ---------- Chips de estado a mostrar ----------
    const estadoEntries = useMemo(() => {
        if (!allowedEstados || allowedEstados.length === 0) return Object.entries(ESTADO_CONFIG);
        return Object.entries(ESTADO_CONFIG).filter(([k]) => allowedEstados.includes(k));
    }, [allowedEstados]);

    // ============================
    // RENDER
    // ============================
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <GlobalStyles styles={calendarStyles} />
            <Box sx={{ p: { xs: 2, md: 3 }, height: '100vh', display: 'flex', flexDirection: 'column' }}>
                <Paper elevation={0} sx={{
                    p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider',
                    borderRadius: 4, bgcolor: '#fafafa', flex: 1,
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                }}>
                    {/* ---------- Header ---------- */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3, flexShrink: 0 }}>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <CalendarMonthIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{title}</Typography>
                                    <Badge badgeContent={stats.total} color="primary" />
                                </Box>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
                                {filterBadges.map((b, i) => (
                                    <Chip key={i} label={b.label} size="small" color={b.color || 'default'} variant="outlined" sx={{ fontWeight: 600 }} />
                                ))}
                            </Stack>
                        </Box>

                        <Stack direction="row" spacing={1}>
                            {allowExport && (
                                <Tooltip title="Exportar a CSV">
                                    <span>
                                        <IconButton onClick={handleExport} disabled={filteredEvents.length === 0}
                                            sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                                            <DownloadIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            <Tooltip title="Actualizar agenda">
                                <IconButton onClick={handleRefresh}
                                    sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearFilters}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                                Limpiar todo
                            </Button>
                        </Stack>
                    </Stack>

                    {/* ---------- Métricas ---------- */}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                        gap: 2, mb: 3, flexShrink: 0,
                    }}>
                        <MetricCard title="TOTAL VISIBLES" value={stats.total} subtitle="Según filtros aplicados" icon={<CalendarMonthIcon />} color="primary.main" />
                        <MetricCard title="EN PROCESO" value={stats.enProceso} subtitle="Viajes activos" icon={<DirectionsBusIcon />} color="success.main" />
                        <MetricCard title="COMPLETADOS" value={stats.completados} subtitle="Servicios finalizados" icon={<EventAvailableIcon />} color="info.main" />
                        <MetricCard title="PRÓXIMOS 24H" value={stats.proximos} subtitle="Inician en 24 horas" icon={<TimelineIcon />} color="warning.main" />
                    </Box>

                    {/* ---------- Chips de estado ---------- */}
                    <Box sx={{ mb: 2.5, flexShrink: 0 }}>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            {estadoEntries.map(([key, config]) => (
                                <FilterChip
                                    key={key}
                                    label={config.label}
                                    active={estadoFilter === key}
                                    onClick={() => setEstadoFilter(estadoFilter === key ? 'TODOS' : key)}
                                    icon={config.icon}
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* ---------- Panel filtros avanzados ---------- */}
                    <Paper elevation={0} sx={{
                        p: 2.5, mb: 2.5, border: '1px solid', borderColor: 'divider',
                        borderRadius: 3, bgcolor: 'background.paper', flexShrink: 0,
                    }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: filtersExpanded ? 2 : 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FilterAltIcon fontSize="small" color="primary" /> Filtros avanzados
                            </Typography>
                            <IconButton size="small" onClick={() => setFiltersExpanded(!filtersExpanded)}>
                                <FilterAltIcon fontSize="small"
                                    sx={{ transform: filtersExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
                            </IconButton>
                        </Stack>
                        {filtersExpanded && (
                            <Fade in={filtersExpanded}>
                                <Box>
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr 1fr auto' },
                                        gap: 2, alignItems: 'flex-start',
                                    }}>
                                        <TextField size="small" label="Buscar" placeholder="Código, ruta, placa, conductor..."
                                            defaultValue={search} onChange={handleSearchChange}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }} />
                                        <DatePicker label="Fecha desde" value={dateRange.start}
                                            onChange={(v) => setDateRange((prev) => ({ ...prev, start: v }))}
                                            renderInput={(params) => <TextField {...params} size="small" fullWidth />} />
                                        <DatePicker label="Fecha hasta" value={dateRange.end}
                                            onChange={(v) => setDateRange((prev) => ({ ...prev, end: v }))}
                                            renderInput={(params) => <TextField {...params} size="small" fullWidth />} />
                                        <Button variant="contained" onClick={handleApplyRange}
                                            disabled={!dateRange.start || !dateRange.end}
                                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, minWidth: 140 }}>
                                            Aplicar rango
                                        </Button>
                                    </Box>
                                    {appliedRange && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                            📅 Rango aplicado: {appliedRange.start} hasta {appliedRange.end}
                                        </Typography>
                                    )}
                                </Box>
                            </Fade>
                        )}
                    </Paper>

                    {/* ---------- Loading / Error ---------- */}
                    {(isLoading || isFetching) && (
                        <Box sx={{ mb: 2, flexShrink: 0 }}>
                            <LinearProgress sx={{ borderRadius: 2 }} />
                        </Box>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2, flexShrink: 0 }}
                            action={<Button color="error" size="small" onClick={handleRefresh}>Reintentar</Button>}>
                            Error al cargar la agenda de manifiestos.
                        </Alert>
                    )}

                    {/* ---------- Calendario ---------- */}
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {filteredEvents.length === 0 && !isLoading && eventsData.length > 0 && (
                            <Alert severity="info" sx={{ mb: 2 }}>No hay manifiestos para mostrar en el rango seleccionado.</Alert>
                        )}
                        <Calendar
                            culture="es"
                            localizer={localizer}
                            events={filteredEvents}
                            messages={CALENDAR_MESSAGES}
                            startAccessor="start"
                            endAccessor="end"
                            view={calendarView}
                            date={calendarDate}
                            onView={setCalendarView}
                            onNavigate={setCalendarDate}
                            onSelectEvent={setSelectedEvent}
                            components={{ event: EventComponent }}
                            popup
                            views={['month', 'week', 'day', 'agenda']}
                            formats={{
                                dayHeaderFormat: (date) => format(date, "EEEE dd 'de' MMMM", { locale: es }),
                                dayRangeHeaderFormat: ({ start, end }) =>
                                    `${format(start, 'dd MMM', { locale: es })} - ${format(end, 'dd MMM yyyy', { locale: es })}`,
                                agendaDateFormat: (date) => format(date, 'dd/MM/yyyy', { locale: es }),
                                agendaTimeFormat: (date) => format(date, 'HH:mm', { locale: es }),
                            }}
                            eventPropGetter={(event) => {
                                const estado = getEstadoConfig(event.resource.manest);
                                return {
                                    style: {
                                        backgroundColor: estado.bg,
                                        color: estado.color,
                                        borderLeft: `4px solid ${estado.border}`,
                                        borderRadius: 10,
                                        padding: '6px 8px',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                    },
                                };
                            }}
                            dayPropGetter={(date) => ({
                                style: { backgroundColor: filteredEvents.some((e) => isSameDay(e.start, date)) ? '#f8f9ff' : undefined },
                            })}
                        />
                    </Box>
                </Paper>

                {/* ---------- Dialog detalle ---------- */}
                <AgendaCalendarDetailDialog event={selectedEvent} onClose={() => setSelectedEvent(null)} />
            </Box>
        </LocalizationProvider>
    );
};

export default AgendaCalendarBase;